import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/UserAuthContext';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  Activity, 
  Settings,
  Trash2,
  Edit2,
  Copy,
  Check,
  X,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  Clock,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const CompanyAdmin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('team');
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  
  // Form states
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    full_name: '',
    role_id: '',
    role_name: ''
  });
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role_id: '',
    role_name: ''
  });

  const userId = user?.user_id;

  // Check permission on mount
  useEffect(() => {
    if (!userId) return;
    
    const checkPermission = async () => {
      try {
        const res = await fetch(`${API_URL}/api/company-admin/check-permission?user_id=${userId}`);
        const data = await res.json();
        setHasPermission(data.has_permission);
        if (data.has_permission) {
          setCompanyInfo({
            role: data.role,
            company_name: data.company_name,
            industry_template: data.industry_template
          });
        }
      } catch (error) {
        console.error('Permission check failed:', error);
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkPermission();
  }, [userId]);

  // Fetch data when tab changes
  const fetchData = useCallback(async () => {
    if (!userId || !hasPermission) return;
    
    try {
      if (activeTab === 'team') {
        const [teamRes, rolesRes] = await Promise.all([
          fetch(`${API_URL}/api/company-admin/team?user_id=${userId}`),
          fetch(`${API_URL}/api/company-admin/available-roles?user_id=${userId}`)
        ]);
        const teamData = await teamRes.json();
        const rolesData = await rolesRes.json();
        
        setTeamMembers(teamData.members || []);
        setPendingInvites(teamData.pending_invites || []);
        setAvailableRoles(rolesData.roles || []);
      } else if (activeTab === 'activity') {
        const res = await fetch(`${API_URL}/api/company-admin/activity?user_id=${userId}`);
        const data = await res.json();
        setActivityLog(data.activities || []);
      } else if (activeTab === 'stats') {
        const res = await fetch(`${API_URL}/api/company-admin/stats?user_id=${userId}`);
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    }
  }, [userId, hasPermission, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create new team member with credentials
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserForm.email || !newUserForm.full_name || !newUserForm.role_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/company-admin/team/create?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUserForm)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Team member created successfully');
        setCreatedCredentials({
          email: data.email,
          password: data.temporary_password,
          name: newUserForm.full_name
        });
        setNewUserForm({ email: '', full_name: '', role_id: '', role_name: '' });
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  // Send invite
  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.role_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/company-admin/team/invite?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inviteForm)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Invitation sent to ${inviteForm.email}`);
        setShowInviteModal(false);
        setInviteForm({ email: '', role_id: '', role_name: '' });
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to send invite');
      }
    } catch (error) {
      toast.error('Failed to send invite');
    }
  };

  // Update member role
  const handleUpdateRole = async (memberId, roleId, roleName) => {
    try {
      const res = await fetch(`${API_URL}/api/company-admin/team/${memberId}/role?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role_id: roleId, role_name: roleName })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Role updated successfully');
        setShowRoleModal(false);
        setSelectedMember(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  // Remove member
  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/company-admin/team/${memberId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Team member removed');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to remove member');
      }
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  // Cancel invite
  const handleCancelInvite = async (email) => {
    try {
      const res = await fetch(`${API_URL}/api/company-admin/team/invite/${encodeURIComponent(email)}?user_id=${userId}`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Invitation cancelled');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to cancel invite');
      }
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400 mb-4">
            You don't have permission to access the Company Admin panel. 
            This feature is only available to company owners and administrators.
          </p>
          <a 
            href="/dashboard" 
            className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Building2 size={32} className="text-blue-500" />
          <h1 className="text-2xl md:text-3xl font-bold text-white">Team Admin</h1>
        </div>
        <p className="text-gray-400">
          Manage your team members, roles, and permissions for {companyInfo?.company_name || 'your company'}
        </p>
        {companyInfo?.role && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
            <Shield size={14} />
            {companyInfo.role}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'team', label: 'Team Members', icon: Users },
          { id: 'activity', label: 'Activity Log', icon: Activity },
          { id: 'stats', label: 'Statistics', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-white text-black' 
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Team Members Tab */}
      {activeTab === 'team' && (
        <div className="space-y-6">
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowAddUserModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              data-testid="add-user-btn"
            >
              <UserPlus size={18} />
              Create Team Member
            </button>
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              data-testid="invite-user-btn"
            >
              <Mail size={18} />
              Send Invite
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>

          {/* Team Members List */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">Active Team Members ({teamMembers.length})</h3>
            </div>
            
            {teamMembers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users size={48} className="mx-auto mb-4 opacity-50" />
                <p>No team members yet. Add your first team member above.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {teamMembers.map(member => (
                  <div 
                    key={member.user_id} 
                    className="p-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                    data-testid={`team-member-${member.user_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {member.full_name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-white font-medium">{member.full_name || 'No Name'}</p>
                        <p className="text-gray-400 text-sm">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm">
                        {member.company_role || 'Member'}
                      </span>
                      {member.user_id !== userId && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedMember(member);
                              setShowRoleModal(true);
                            }}
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                            title="Change Role"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded-lg transition-colors"
                            title="Remove Member"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                      {member.user_id === userId && (
                        <span className="text-xs text-blue-400">(You)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Invites */}
          {pendingInvites.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-800">
                <h3 className="text-lg font-semibold text-white">Pending Invitations ({pendingInvites.length})</h3>
              </div>
              <div className="divide-y divide-gray-800">
                {pendingInvites.map(invite => (
                  <div 
                    key={invite.email} 
                    className="p-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-yellow-600/30 rounded-full flex items-center justify-center">
                        <Clock size={20} className="text-yellow-500" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{invite.email}</p>
                        <p className="text-gray-400 text-sm">Invited as {invite.role_name}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvite(invite.email)}
                      className="px-3 py-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
            <button
              onClick={fetchData}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <RefreshCw size={18} />
            </button>
          </div>
          
          {activityLog.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Activity size={48} className="mx-auto mb-4 opacity-50" />
              <p>No activity recorded yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800 max-h-[600px] overflow-y-auto">
              {activityLog.map((activity, idx) => (
                <div 
                  key={idx} 
                  className="p-4 hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-blue-600/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Activity size={14} className="text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white">
                        <span className="font-medium">{activity.actor_name}</span>
                        {' '}
                        <span className="text-gray-400">
                          {activity.action === 'created_user' && 'created a new team member'}
                          {activity.action === 'invited_user' && 'invited'}
                          {activity.action === 'removed_user' && 'removed'}
                          {activity.action === 'changed_role' && 'changed role for'}
                          {activity.action === 'cancelled_invite' && 'cancelled invite for'}
                          {activity.action === 'joined_company' && 'joined the company'}
                        </span>
                        {activity.target_user && (
                          <span className="text-gray-300"> {activity.target_user}</span>
                        )}
                      </p>
                      {activity.details && (
                        <p className="text-sm text-gray-500 mt-1">{activity.details}</p>
                      )}
                      <p className="text-xs text-gray-600 mt-2">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users size={24} className="text-blue-500" />
              <span className="text-gray-400">Total Members</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.total_members}</p>
          </div>
          
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Mail size={24} className="text-yellow-500" />
              <span className="text-gray-400">Pending Invites</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.pending_invites}</p>
          </div>
          
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Activity size={24} className="text-green-500" />
              <span className="text-gray-400">Recent Activity (7d)</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.recent_activity_count}</p>
          </div>
          
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Shield size={24} className="text-purple-500" />
              <span className="text-gray-400">Roles in Use</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {Object.keys(stats.role_distribution || {}).length}
            </p>
          </div>
          
          {/* Role Distribution */}
          {stats.role_distribution && Object.keys(stats.role_distribution).length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 md:col-span-2 lg:col-span-4">
              <h3 className="text-lg font-semibold text-white mb-4">Role Distribution</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(stats.role_distribution).map(([role, count]) => (
                  <div 
                    key={role}
                    className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                  >
                    <span className="text-gray-300 truncate">{role}</span>
                    <span className="text-white font-semibold ml-2">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Create Team Member</h3>
              <button 
                onClick={() => {
                  setShowAddUserModal(false);
                  setCreatedCredentials(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            {createdCredentials ? (
              <div className="p-6">
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <Check size={20} />
                    <span className="font-semibold">User Created Successfully!</span>
                  </div>
                  <p className="text-green-300 text-sm">Share these credentials with {createdCredentials.name}</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm">Email</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={createdCredentials.email}
                        readOnly
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                      />
                      <button
                        onClick={() => copyToClipboard(createdCredentials.email)}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
                      >
                        <Copy size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-gray-400 text-sm">Temporary Password</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={createdCredentials.password}
                        readOnly
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(createdCredentials.password)}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
                      >
                        <Copy size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      copyToClipboard(`Email: ${createdCredentials.email}\nPassword: ${createdCredentials.password}`);
                    }}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Copy All Credentials
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setCreatedCredentials(null);
                  }}
                  className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors mt-4"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                <div>
                  <label className="text-gray-400 text-sm">Full Name *</label>
                  <input
                    type="text"
                    value={newUserForm.full_name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, full_name: e.target.value })}
                    className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                    placeholder="John Doe"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm">Email *</label>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                    placeholder="john@company.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm">Role *</label>
                  <div className="relative mt-1">
                    <select
                      value={newUserForm.role_id}
                      onChange={(e) => {
                        const role = availableRoles.find(r => r.id === e.target.value);
                        setNewUserForm({ 
                          ...newUserForm, 
                          role_id: e.target.value,
                          role_name: role?.name || ''
                        });
                      }}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 appearance-none"
                      required
                    >
                      <option value="">Select a role</option>
                      {availableRoles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name} {role.sub_company ? `(${role.sub_company})` : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                
                <div className="pt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Create User
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Send Invitation</h3>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSendInvite} className="p-6 space-y-4">
              <div>
                <label className="text-gray-400 text-sm">Email *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                  placeholder="newmember@company.com"
                  required
                />
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Role *</label>
                <div className="relative mt-1">
                  <select
                    value={inviteForm.role_id}
                    onChange={(e) => {
                      const role = availableRoles.find(r => r.id === e.target.value);
                      setInviteForm({ 
                        ...inviteForm, 
                        role_id: e.target.value,
                        role_name: role?.name || ''
                      });
                    }}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 appearance-none"
                    required
                  >
                    <option value="">Select a role</option>
                    {availableRoles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name} {role.sub_company ? `(${role.sub_company})` : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <p className="text-gray-500 text-sm">
                An email will be sent to this address with instructions to join your team.
              </p>
              
              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && selectedMember && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Change Role</h3>
              <button 
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedMember(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {selectedMember.full_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedMember.full_name}</p>
                  <p className="text-gray-400 text-sm">Current: {selectedMember.company_role}</p>
                </div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">New Role</label>
                <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                  {availableRoles.map(role => (
                    <button
                      key={role.id}
                      onClick={() => handleUpdateRole(selectedMember.user_id, role.id, role.name)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedMember.role_id === role.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <p className="font-medium">{role.name}</p>
                      {role.sub_company && (
                        <p className="text-sm opacity-70">{role.sub_company}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedMember(null);
                }}
                className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAdmin;
